import { computed, Ref, ref, watch } from "vue";

import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import * as Sentry from "@sentry/vue";
import { defineStore, storeToRefs } from "pinia";
import getHasuraToken from "@/graphql/services/getHasuraToken";
import axios from "axios";
import gaiupApolloClient, { wsClient } from "@/graphql/services/ApolloClient";

const useHasuraStore = defineStore("Hasura", () => {
  const useAuthState = useAuthStateStore();
  const { hasAccessAllProjects, user } = storeToRefs(useAuthState);
  const hasuraToken = ref<string>();
  const error = ref<boolean>(false);
  const loading = ref<boolean>(true);
  const status: Ref<"loading" | "error" | "authenticated" | "unauthenticated"> = computed(() => {
    if (loading.value) {
      return "loading";
    } else if (error.value) {
      return "error";
    } else if (hasuraToken.value) {
      return "authenticated";
    }

    return "unauthenticated";
  });

  // Two mints can be in flight at once: the user-watcher mint (aborted on the next user change)
  // and retry(), which has no abort signal. Whoever started LAST wins — a superseded response,
  // success or failure, is discarded — so a mint carrying the previous tenant's identity can
  // never land after the correct one (RTLCA-5588).
  let fetchSequence = 0;

  const fetchToken = async (accessToken: string, signal?: AbortSignal) => {
    const sequence = ++fetchSequence;
    try {
      const token = await getHasuraToken(accessToken, signal);
      if (sequence !== fetchSequence) return;
      hasuraToken.value = token;
    } catch (e) {
      // If request was aborted due to navigation/cleanup, don't report as error
      if (axios.isCancel(e) || (axios.isAxiosError(e) && e.code === "ERR_CANCELED")) {
        return;
      }

      if (sequence !== fetchSequence) return;

      // If not able to get hasura token
      console.error(e);
      error.value = true;

      // Level on the capture, not the scope: getCurrentScope() outlives this call, so a level set
      // there tagged every later report in the session fatal too.
      Sentry.captureException(new Error("Could not get Hasura token", { cause: e }), { level: "fatal" });
    }

    loading.value = false;
  };

  watch(
    user,
    async (newUser, _, onCleanup) => {
      // Get the access token from the user
      const accessToken = newUser?.accessToken;

      if (typeof accessToken === "string" && accessToken.length > 0) {
        const abortController = new AbortController();

        onCleanup(() => {
          abortController.abort();
        });

        await fetchToken(accessToken, abortController.signal);
      }
    },
    {
      immediate: true,
    }
  );

  const retry = async () => {
    const accessToken = user.value?.accessToken;
    if (typeof accessToken === "string" && accessToken.length > 0) {
      error.value = false;
      loading.value = true;
      await fetchToken(accessToken);
    }
  };

  watch(
    () => hasuraToken.value,
    (newHasuraToken) => {
      if (typeof newHasuraToken === "string" && newHasuraToken.length > 0) {
        gaiupApolloClient.defaultContext.token = newHasuraToken;
        // The WS socket authenticates once per connection — graphql-ws reads connectionParams
        // only when a socket OPENS — so a token minted for a new tenant never reaches an
        // already-open socket, which keeps serving every subscription as the OLD tenant
        // (RTLCA-5588: previous org's project pushed into the new org's list). Terminate AFTER
        // the context assignment above: the client reconnects on its own (lazy, infinite
        // retries) and re-evaluates connectionParams against the token just written, restoring
        // every active subscription under the new identity.
        wsClient.terminate();
      }
    }
  );

  watch(
    hasAccessAllProjects,
    (newHasAccessAllProjects) => {
      if (newHasAccessAllProjects === undefined) return;
      gaiupApolloClient.defaultContext.hasAccessAllProjects = newHasAccessAllProjects;
      wsClient.terminate();
    },
    { immediate: true }
  );

  // Resolves with the Hasura JWT once it lands, or null if auth errored / didn't settle in time.
  // Lets best-effort callers (e.g. the recent-projects tracker) avoid firing a request before the
  // token has been pushed into Apollo's defaultContext.
  const awaitHasuraToken = (timeoutMs = 10000): Promise<string | null> => {
    if (hasuraToken.value) return Promise.resolve(hasuraToken.value);
    if (error.value) return Promise.resolve(null);
    return new Promise((resolve) => {
      const handles: { timer?: ReturnType<typeof setTimeout>; stop?: () => void } = {};
      const finish = (value: string | null) => {
        handles.stop?.();
        if (handles.timer) clearTimeout(handles.timer);
        resolve(value);
      };
      handles.stop = watch([hasuraToken, error], ([newToken, newError]) => {
        if (newToken) finish(newToken);
        else if (newError) finish(null);
      });
      handles.timer = setTimeout(() => finish(null), timeoutMs);
    });
  };

  return { status, retry, hasuraToken, awaitHasuraToken };
});

export default useHasuraStore;
